/**
 * AI Preview Component
 * Displays AI-generated search insights with Google-style preview functionality
 */

import React, { useState, useEffect, useCallback, useRef, memo } from 'react';
import { Button, SvgIcon } from '@ui-elements';
import {
  AIPreviewRequest,
  AIPreviewResponse,
  PreviewDisplayState,
  AIPreviewError,
  AIPreviewState,
  AIStreamingConfig,
  AccessibilityState,
  AICitation,
  AIModeSeed,
} from './types';
import { AIStreamingService } from '../../../../services/ai-streaming.service';
import { renderMarkdown } from './markdown';
import SourcesPanel from '../sources-panel';
import styles from './index.module.scss';

interface AIPreviewProps {
  searchQuery: string;
  isVisible: boolean;
  sessionId?: string;
  onError?: (error: AIPreviewError) => void;
  onComplete?: (response: AIPreviewResponse) => void;
  // When provided, "Show More" hands the conversation off to AI Mode
  // (Google-style) instead of expanding in place.
  onShowMore?: (seed: AIModeSeed) => void;
  // Test props for TDD
  mockResponse?: string;
  mockError?: AIPreviewError;
  isLoading?: boolean;
  isStreaming?: boolean;
  initiallyExpanded?: boolean;
  streamingChunks?: string[];
  retryCount?: number;
}

const AIPreview: React.FC<AIPreviewProps> = memo(
  ({
    searchQuery,
    isVisible,
    sessionId,
    onError,
    onComplete,
    onShowMore,
    mockResponse,
    mockError,
    isLoading: propIsLoading,
    isStreaming: propIsStreaming,
    initiallyExpanded = false,
    streamingChunks = [],
    retryCount = 0,
  }) => {
    // State management
    const [displayState, setDisplayState] = useState<PreviewDisplayState>({
      isVisible: false,
      isExpanded: initiallyExpanded,
      previewLines: [],
      totalLines: [],
      isStreaming: false,
      streamingCursor: false,
      animationState: 'idle',
    });

    const [aiState, setAiState] = useState<AIPreviewState>(AIPreviewState.IDLE);
    const [content, setContent] = useState<string>('');
    const [streamedContent, setStreamedContent] = useState<string>('');
    const [sources, setSources] = useState<AICitation[]>([]);
    const [error, setError] = useState<AIPreviewError | null>(null);
    const [accessibilityState, setAccessibilityState] =
      useState<AccessibilityState>({
        ariaLabel: 'AI Search Insights',
        ariaLiveRegion: '',
        focusManagement: 'auto',
        reducedMotion: false,
        hasKeyboardFocus: false,
      });

    // Refs
    const streamingServiceRef = useRef<AIStreamingService | null>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const retryCountRef = useRef<number>(retryCount);
    // Last query a stream was started for. The trigger effect depends on
    // callbacks whose identity changes every parent render (onComplete etc.),
    // so without this guard each render refires the request — observed as
    // 6+ duplicate streams per search on staging.
    const lastQueryRef = useRef<string | null>(null);
    // Session id the stream actually used (prop may lag a render behind the
    // store) — handed to AI Mode so follow-ups continue the same session.
    const usedSessionIdRef = useRef<string | null>(null);

    // Constants
    const MAX_PREVIEW_LINES = 7;
    const MAX_RETRY_ATTEMPTS = 3;

    // Environment configuration. Same convention as the recs widgets: a
    // build-time env override, else the AiApiBase/RecommendationsApiBase
    // window global set by the site, else the staging AI API.
    const w = typeof window !== 'undefined' ? (window as any) : ({} as any);
    const aiApiBase = (
      w.AiApiBase ||
      w.RecommendationsApiBase ||
      'https://ai-api-staging.inchcape-web.au'
    ).replace(/\/$/, '');
    const streamingConfig: AIStreamingConfig = {
      apiEndpoint:
        process.env.REACT_APP_AI_API_ENDPOINT ||
        `${aiApiBase}/chat/generate/stream`,
      apiKey: process.env.REACT_APP_AI_API_KEY || '',
      // Only send Authorization when a key is configured — the chat endpoint
      // is unauthenticated and a spurious header just forces CORS preflight.
      headers: process.env.REACT_APP_AI_API_KEY
        ? { Authorization: `Bearer ${process.env.REACT_APP_AI_API_KEY}` }
        : {},
      timeout: parseInt(
        process.env.REACT_APP_AI_PREVIEW_TIMEOUT || '30000',
        10
      ),
      maxRetries: 3,
    };

    // Initialize streaming service
    useEffect(() => {
      streamingServiceRef.current = new AIStreamingService(streamingConfig);

      return () => {
        streamingServiceRef.current?.cancelCurrentRequest();
      };
    }, []);

    // Handle reduced motion preference
    useEffect(() => {
      if (typeof window === 'undefined') return;

      const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      setAccessibilityState((prev) => ({
        ...prev,
        reducedMotion: mediaQuery.matches,
      }));

      const handleChange = (e: MediaQueryListEvent) => {
        setAccessibilityState((prev) => ({
          ...prev,
          reducedMotion: e.matches,
        }));
      };

      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }, []);

    // Process content into lines for preview/expand logic
    const processContentLines = useCallback(
      (
        contentText: string
      ): { previewLines: string[]; totalLines: string[] } => {
        if (!contentText.trim()) {
          return { previewLines: [], totalLines: [] };
        }

        const lines = contentText.split('\n').filter((line) => line.trim());
        const previewLines = lines.slice(0, MAX_PREVIEW_LINES);

        return {
          previewLines,
          totalLines: lines,
        };
      },
      []
    );

    // Handle streaming chunk updates
    const handleStreamingChunk = useCallback(
      (chunk: string, isComplete: boolean) => {
        setStreamedContent(chunk);

        if (isComplete) {
          setContent(chunk);
          setAiState(AIPreviewState.COMPLETE);
          setDisplayState((prev) => ({
            ...prev,
            isStreaming: false,
            streamingCursor: false,
          }));

          // Update accessibility announcement
          setAccessibilityState((prev) => ({
            ...prev,
            ariaLiveRegion: 'AI insights loaded',
          }));
        } else {
          setAiState(AIPreviewState.STREAMING);
          setDisplayState((prev) => ({
            ...prev,
            isStreaming: true,
            streamingCursor: true,
          }));
        }
      },
      []
    );

    // Handle streaming errors
    const handleStreamingError = useCallback(
      (streamError: AIPreviewError) => {
        setError(streamError);
        setAiState(AIPreviewState.ERROR);
        setDisplayState((prev) => ({
          ...prev,
          isStreaming: false,
          streamingCursor: false,
        }));

        // Update accessibility announcement
        setAccessibilityState((prev) => ({
          ...prev,
          ariaLiveRegion: `Error: ${streamError.message}`,
        }));

        onError?.(streamError);
      },
      [onError]
    );

    // Trigger AI request
    const triggerAIRequest = useCallback(async () => {
      if (!searchQuery.trim() || !streamingServiceRef.current) return;

      // Use mock data for testing
      if (mockResponse) {
        handleStreamingChunk(mockResponse, true);
        return;
      }

      if (mockError) {
        handleStreamingError(mockError);
        return;
      }

      try {
        setAiState(AIPreviewState.LOADING);
        setError(null);
        setSources([]);

        const requestSessionId =
          sessionId ||
          (typeof window !== 'undefined'
            ? `session-${Date.now()}`
            : 'ssr-session');
        usedSessionIdRef.current = requestSessionId;
        const request: AIPreviewRequest = {
          prompt: searchQuery,
          session_id: requestSessionId,
          timestamp: new Date(),
          // Same switch as the Show More behaviour: with no AI Mode hand-off
          // (simple AI mode) the answer is all the reader gets, so ask for one
          // that closes instead of inviting a follow-up it cannot accept.
          mode: onShowMore ? 'conversational' : 'oneoff',
        };

        const response = await streamingServiceRef.current.streamAIResponse(
          request,
          handleStreamingChunk,
          handleStreamingError
        );

        setSources(response.metadata?.sources || []);
        onComplete?.(response);
      } catch (err) {
        const aiError: AIPreviewError = {
          type: 'network',
          message: 'Failed to fetch AI insights',
          details: err instanceof Error ? err.message : 'Unknown error',
          isRetryable: true,
        };
        handleStreamingError(aiError);
      }
    }, [
      searchQuery,
      sessionId,
      onShowMore,
      mockResponse,
      mockError,
      handleStreamingChunk,
      handleStreamingError,
      onComplete,
    ]);

    // Update content when streaming changes
    useEffect(() => {
      const currentContent = streamedContent || content;
      if (currentContent) {
        const { previewLines, totalLines } =
          processContentLines(currentContent);
        setDisplayState((prev) => ({
          ...prev,
          previewLines,
          totalLines,
        }));
      }
    }, [streamedContent, content, processContentLines]);

    // Handle search query changes. Only start a stream when the query
    // actually changed — the callback deps churn identity on every parent
    // render, and refiring mid-stream aborts/duplicates requests and keeps
    // the sources from ever rendering.
    useEffect(() => {
      if (isVisible && searchQuery.trim()) {
        setDisplayState((prev) => ({ ...prev, isVisible: true }));
        if (lastQueryRef.current !== searchQuery) {
          lastQueryRef.current = searchQuery;
          triggerAIRequest();
        }
      } else {
        setDisplayState((prev) => ({ ...prev, isVisible: false }));
      }
    }, [searchQuery, isVisible, triggerAIRequest]);

    // Mock streaming for testing
    useEffect(() => {
      if (propIsStreaming && streamingChunks.length > 0) {
        let index = 0;
        const interval = setInterval(() => {
          if (index < streamingChunks.length) {
            handleStreamingChunk(
              streamingChunks.slice(0, index + 1).join(''),
              false
            );
            index += 1;
          } else {
            handleStreamingChunk(streamingChunks.join(''), true);
            clearInterval(interval);
          }
        }, 50); // 50ms throttling as per research

        return () => clearInterval(interval);
      }
    }, [propIsStreaming, streamingChunks, handleStreamingChunk]);

    // Simple AI mode expands the answer in place, so the button must reflect
    // whether anything is actually hidden: the collapsed state is a fixed CSS
    // height and the markdown is rendered in full, so counting source lines
    // says nothing about what the reader can see. Measure instead — and leave
    // the value alone while expanded (nothing overflows then) so the button
    // survives as "Show Less".
    const [isOverflowing, setIsOverflowing] = useState<boolean>(false);
    useEffect(() => {
      if (onShowMore || displayState.isExpanded) return;
      const el = contentRef.current;
      if (!el) return;
      setIsOverflowing(el.scrollHeight > el.clientHeight + 1);
    }, [
      onShowMore,
      displayState.isExpanded,
      streamedContent,
      content,
      aiState,
    ]);

    // Handle expand/collapse toggle
    const toggleExpanded = useCallback(() => {
      const newExpandedState = !displayState.isExpanded;

      setDisplayState((prev) => ({
        ...prev,
        isExpanded: newExpandedState,
        animationState: newExpandedState ? 'expanding' : 'collapsing',
      }));

      // Complete animation after CSS transition
      setTimeout(
        () => {
          setDisplayState((prev) => ({
            ...prev,
            animationState: 'idle',
          }));
        },
        accessibilityState.reducedMotion ? 0 : 300
      );

      // Update accessibility state
      setAccessibilityState((prev) => ({
        ...prev,
        ariaLiveRegion: newExpandedState
          ? 'Content expanded'
          : 'Content collapsed',
      }));
    }, [displayState.isExpanded, accessibilityState.reducedMotion]);

    // Handle retry action
    const handleRetry = useCallback(() => {
      if (retryCountRef.current >= MAX_RETRY_ATTEMPTS) {
        return;
      }

      retryCountRef.current += 1;
      setError(null);
      triggerAIRequest();
    }, [triggerAIRequest]);

    // Handle keyboard events
    const handleKeyDown = useCallback(
      (event: React.KeyboardEvent) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          toggleExpanded();
        }
      },
      [toggleExpanded]
    );

    // Handle focus events
    const handleFocus = useCallback(() => {
      setAccessibilityState((prev) => ({
        ...prev,
        hasKeyboardFocus: true,
      }));
    }, []);

    const handleBlur = useCallback(() => {
      setAccessibilityState((prev) => ({
        ...prev,
        hasKeyboardFocus: false,
      }));
    }, []);

    // Don't render if not visible
    if (!isVisible || !displayState.isVisible) {
      return null;
    }

    const isActuallyLoading =
      propIsLoading || aiState === AIPreviewState.LOADING;
    const isActuallyStreaming = propIsStreaming || displayState.isStreaming;
    const hasError = mockError || error;
    // With an AI Mode hand-off available, always offer Show More once the
    // answer completes (even short answers benefit from follow-ups); in simple
    // AI mode it expands in place, so only offer it when there is more to see.
    const showShowMoreButton = onShowMore
      ? aiState === AIPreviewState.COMPLETE &&
        Boolean(streamedContent || content)
      : isOverflowing || displayState.isExpanded;
    // The collapsed state is a fixed height reserved so the layout doesn't jump
    // while the answer streams. Once a short simple-mode answer has finished
    // with nothing hidden, release it rather than pad it out with blank space.
    const isFullyShown =
      displayState.isExpanded ||
      (!onShowMore && aiState === AIPreviewState.COMPLETE && !isOverflowing);
    const displayContent = displayState.isExpanded
      ? displayState.totalLines
      : displayState.previewLines;

    return (
      <div
        className={`${styles.aiPreviewContainer} ${styles.positionedBelowSearchBar}`}
        role="region"
        aria-label={accessibilityState.ariaLabel}
        data-testid="ai-preview-container"
      >
        {/* Header: AI icon + clear title (Google AI Overview style) */}
        <div className={styles.header}>
          <span className={styles.headerIcon} aria-hidden="true">
            ✦
          </span>
          <span className={styles.headerTitle}>AI Overview</span>
        </div>

        {/* Live region for screen reader announcements */}
        <div
          role="status"
          aria-live="polite"
          aria-atomic="false"
          className={styles.srOnly}
        >
          {accessibilityState.ariaLiveRegion}
        </div>

        {/* Error region for urgent announcements */}
        {hasError && (
          <div role="alert" aria-live="assertive" className={styles.srOnly}>
            {hasError.message}
          </div>
        )}

        {/* Main content */}
        {isActuallyLoading && (
          <div
            className={`${styles.loading} ${
              typeof window !== 'undefined' &&
              navigator.connection?.effectiveType === '2g'
                ? styles.slowNetwork
                : ''
            }`}
            data-testid="ai-preview-loading"
          >
            <div className={styles.loadingSpinner} />
            <span>
              Loading AI insights
              {typeof window !== 'undefined' &&
                navigator.connection?.effectiveType === '2g' && (
                  <span className={styles.slowNetworkNote}>
                    {' '}
                    (Loading may take longer on slow connections)
                  </span>
                )}
            </span>
          </div>
        )}

        {hasError && (
          <div
            className={`${styles.errorState} ${styles.mobileErrorFormat}`}
            data-testid="ai-preview-error"
          >
            <div className={styles.errorMessage}>
              {hasError.type === 'network'
                ? 'Connection failed'
                : hasError.message}
            </div>

            {hasError.isRetryable &&
            retryCountRef.current < MAX_RETRY_ATTEMPTS ? (
              <>
                <Button
                  type="primary"
                  buttonSize="medium"
                  className={`${styles.retryButton} ${styles.mobilePrimaryButton}`}
                  onClick={handleRetry}
                  disabled={isActuallyLoading}
                >
                  Retry
                </Button>
                {hasError.retryAfter && (
                  <div
                    className={styles.retryCountdown}
                    data-testid="retry-countdown"
                  >
                    Please wait {hasError.retryAfter} seconds before retrying
                  </div>
                )}
              </>
            ) : retryCountRef.current >= MAX_RETRY_ATTEMPTS ? (
              <div className={styles.maxRetriesMessage}>
                Maximum retry attempts reached. Please try again later.
              </div>
            ) : null}
          </div>
        )}

        {!isActuallyLoading && !hasError && displayContent.length > 0 && (
          <div className={styles.contentContainer}>
            <div className={styles.mainColumn}>
              <div
                ref={contentRef}
                className={`${styles.content} ${
                  isFullyShown ? styles.expanded : styles.collapsed
                } ${
                  displayState.animationState === 'expanding'
                    ? styles.expanding
                    : ''
                } ${
                  displayState.animationState === 'collapsing'
                    ? styles.collapsing
                    : ''
                } ${displayState.isStreaming ? styles.streaming : ''}`}
                id="ai-preview-content"
                data-testid="ai-preview-content"
                style={{
                  transition: accessibilityState.reducedMotion
                    ? 'none'
                    : 'max-height 0.3s ease-out',
                  willChange: accessibilityState.reducedMotion
                    ? 'auto'
                    : 'max-height',
                  animation: accessibilityState.reducedMotion
                    ? 'none'
                    : undefined,
                }}
              >
                <div
                  className={styles.markdown}
                  // Sanitized in renderMarkdown: input is HTML-escaped, then a
                  // DOMPurify pass with a strict tag/attr allowlist.
                  dangerouslySetInnerHTML={{
                    __html: renderMarkdown(streamedContent || content),
                  }}
                />

                {isActuallyStreaming && (
                  <span
                    className={`${styles.typingCursor} ${styles.blinkingCursor}`}
                    data-testid="typing-cursor"
                    aria-hidden="true"
                  >
                    ▌
                  </span>
                )}
              </div>

              {/* Streaming indicator for tests */}
              {isActuallyStreaming && (
                <div
                  data-testid="ai-preview-streaming"
                  className={styles.srOnly}
                >
                  AI content streaming...
                </div>
              )}

              {onShowMore ? (
                // Reserved slot: the button area holds its height while the
                // answer streams so completion doesn't pop the layout.
                <div className={styles.showMoreSlot}>
                  {showShowMoreButton && (
                    // Google-style hand-off: continue in AI Mode.
                    <Button
                      type="secondary"
                      buttonSize="medium"
                      className={styles.expandButton}
                      onClick={() =>
                        onShowMore({
                          question: searchQuery,
                          answer: streamedContent || content,
                          sources,
                          sessionId: usedSessionIdRef.current || sessionId,
                        })
                      }
                      onFocus={handleFocus}
                      onBlur={handleBlur}
                      aria-label="Show more — continue in AI Mode"
                      data-testid="ai-preview-show-more"
                    >
                      Show More
                    </Button>
                  )}
                </div>
              ) : (
                showShowMoreButton && (
                  <Button
                    type="secondary"
                    buttonSize="medium"
                    className={`${styles.expandButton} ${
                      accessibilityState.hasKeyboardFocus
                        ? styles.focusVisible
                        : ''
                    }`}
                    onClick={toggleExpanded}
                    onKeyDown={handleKeyDown}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    aria-expanded={displayState.isExpanded}
                    aria-controls="ai-preview-content"
                    data-testid="ai-preview-show-more"
                    aria-describedby={`ai-preview-description-${
                      displayState.isExpanded ? 'collapse' : 'expand'
                    }`}
                  >
                    {displayState.isExpanded ? 'Show Less' : 'Show More'}
                  </Button>
                )
              )}

              {/* Hidden descriptions for screen readers */}
              <div id="ai-preview-description-expand" className={styles.srOnly}>
                Show full AI response content
              </div>
              <div
                id="ai-preview-description-collapse"
                className={styles.srOnly}
              >
                Collapse to preview format
              </div>
            </div>

            {/* Sources panel — its 300px column is reserved from the start of
                streaming (skeleton) so the answer text never reflows when the
                real sources arrive. */}
            {isActuallyStreaming && (
              <nav
                className={styles.sourcesPanel}
                aria-hidden="true"
                data-testid="ai-preview-sources-skeleton"
              >
                <span className={styles.sourcesLabel}>Sources</span>
                <ul className={styles.sourcesList}>
                  {[0, 1, 2].map((i) => (
                    <li key={i} className={styles.sourceSkeleton} />
                  ))}
                </ul>
              </nav>
            )}
            {!isActuallyStreaming && sources.length > 0 && (
              <SourcesPanel sources={sources} />
            )}
          </div>
        )}
      </div>
    );
  }
);

AIPreview.displayName = 'AIPreview';

export default AIPreview;
